import { calcAge } from "./ageUtils";

export const DEFAULT_CONSENT_FORM_TEMPLATE = `<!DOCTYPE html>
<html lang="en">

<head>
    <meta charset="UTF-8">
    <title>IPD Consent Form</title>

    <style>
        body {
            font-family: Arial, sans-serif;
            padding: 32px;
            color: #111;
        }

        h1 {
            margin: 0 0 4px;
            font-size: 20px;
        }

        h2 {
            font-size: 14px;
            margin-top: 24px;
            border-bottom: 1px solid #999;
            padding-bottom: 4px;
        }

        table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 8px;
            font-size: 12px;
        }

        td,
        th {
            padding: 4px 6px;
            border: 1px solid #ddd;
            vertical-align: top;
        }

        th {
            background: #f5f5f5;
            font-weight: 600;
            text-align: left;
        }

        td.k {
            background: #f5f5f5;
            width: 30%;
            font-weight: 600;
        }

        p {
            font-size: 12px;
            line-height: 1.5;
        }

        .facility-address {
            font-size: 11px;
            color: #666;
        }

        .consent-box {
            border: 1px solid #ccc;
            padding: 10px 12px;
            margin-top: 10px;
            background: #fafafa;
        }

        .consent-box p {
            margin: 0;
        }

        .document-table {
            margin-top: 12px;
        }

        .document-table th {
            background: #f5f5f5;
        }

        .document-note {
            font-size: 11px;
            color: #555;
            margin-top: 6px;
        }

        .sig {
            margin-top: 48px;
            display: flex;
            justify-content: space-between;
            font-size: 12px;
        }

        .sig div {
            width: 40%;
            text-align: center;
            border-top: 1px solid #333;
            padding-top: 4px;
        }

        @media print {
            body {
                padding: 20px;
            }
        }
    </style>

</head>

<body>

    <h1>{{facilityName}} — IPD Consent Form</h1>

    {{facilityAddressSection}}

    <h2>Patient Demographics</h2>

    <table>

        <tr>
            <td class="k">UHID</td>
            <td>{{uhid}}</td>
        </tr>

        <tr>
            <td class="k">Name</td>
            <td>{{title}} {{name}}</td>
        </tr>

        <tr>
            <td class="k">Gender / Age</td>
            <td>{{gender}} / {{age}}</td>
        </tr>

        <tr>
            <td class="k">Date of Birth</td>
            <td>{{dob}}</td>
        </tr>

        <tr>
            <td class="k">Mobile</td>
            <td>{{primaryMobile}}</td>
        </tr>

        <tr>
            <td class="k">Nationality / Religion</td>
            <td>{{nationality}} / {{religion}}</td>
        </tr>

        <tr>
            <td class="k">Address</td>
            <td>{{address}}</td>
        </tr>

        <tr>
            <td class="k">Admitting Doctor</td>
            <td>{{admittingDoctor}}</td>
        </tr>

        <tr>
            <td class="k">Financial Class / Payer</td>
            <td>{{financialClass}} / {{payer}}</td>
        </tr>

    </table>

    {{documentIdProofSection}}

    <h2>Consent for Admission &amp; Treatment</h2>

    <p>
        I, the undersigned, voluntarily consent to admission and to such diagnostic procedures,
        treatment, anaesthesia, surgery and hospital services as may be considered necessary by
        the treating physicians at <strong>{{facilityName}}</strong>.
        I confirm that the nature, purpose, risks, benefits and alternatives have been explained
        to me in a language I understand.
    </p>

    <p>
        I authorise the hospital to release medical information to my insurer / TPA / payer for
        the purpose of claim processing, and to retain records as required by law.
    </p>


    <div class="sig">
        <div>Patient / Guardian Signature</div>
        <div>Witness Signature</div>
    </div>

</body>

</html>`;

const DOCUMENT_ID_PROOF_HEADING = "Consent for Submission of Identity";

function ss(key, fallback = "") {
  if (typeof sessionStorage === "undefined") return fallback;
  const value = sessionStorage.getItem(key);
  return value && value !== "null" && value !== "undefined" ? value : fallback;
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function dash(value) {
  const text = String(value ?? "").trim();
  return text || "—";
}

function hasPatientDocuments(patient = {}) {
  return (patient.documents || []).length > 0;
}

function formatDocType(type) {
  return String(type || "").replace(/_/g, " ").trim();
}

function buildDocumentRow(person, type, number) {
  return `<tr><td>${escapeHtml(person)}</td><td>${escapeHtml(dash(formatDocType(type)))}</td><td>${escapeHtml(dash(number))}</td></tr>`;
}

function formatGuarantorPerson(guarantor = {}) {
  const name = String(guarantor.name || "").trim();
  const relation = String(guarantor.relation || "").trim();
  const detail = [name, relation].filter(Boolean).join(" — ");
  return detail ? `Guarantor (${detail})` : "Guarantor";
}

function buildDocumentIdProofRows(patient = {}) {
  const patientRows = (patient.documents || []).map((doc) => (
    buildDocumentRow("Patient", doc?.type, doc?.number)
  ));

  const guarantorRows = (patient.guarantors || [])
    .filter((g) => String(g?.idType || "").trim() || String(g?.idValue || "").trim())
    .map((g) => buildDocumentRow(formatGuarantorPerson(g), g.idType, g.idValue));

  return [...patientRows, ...guarantorRows].join("");
}

function buildDocumentIdProofSection(patient = {}, facilityName = "") {
  if (!hasPatientDocuments(patient)) return "";

  const rows = buildDocumentIdProofRows(patient);
  return `
    <h2>Consent for Submission of Identity / Document ID Proofs</h2>

    <div class="consent-box">

        <p>
            I, the undersigned patient / patient representative, confirm that I have voluntarily
            provided the identity and/or address proof documents listed below to
            <strong>${facilityName}</strong> for the purpose of patient registration, identity
            verification, admission, treatment, billing, insurance/TPA processing and other
            hospital-related requirements.
        </p>

        <p style="margin-top: 8px;">
            I confirm that the documents and information provided by me are true and belong to
            me / the patient / guarantor, as applicable. I understand that the hospital may
            verify, scan, reproduce, store and retain copies of these documents as required for
            hospital records, statutory requirements, insurance/TPA processing and other lawful
            purposes.
        </p>

        <p style="margin-top: 8px;">
            I understand and acknowledge that a signed copy of this consent form will be retained
            by the hospital as evidence that the above identity/document proofs were provided
            with my knowledge and consent.
        </p>

    </div>


    <table class="document-table">

        <thead>
            <tr>
                <th style="width: 25%;">Person</th>
                <th style="width: 35%;">Document Type</th>
                <th style="width: 40%;">Document ID / Number</th>
            </tr>
        </thead>

        <tbody>
            ${rows}
        </tbody>

    </table>

    <p class="document-note">
        Note: Document ID / number may be partially masked in the hospital copy in accordance
        with applicable privacy and data protection requirements.
    </p>
`;
}

function stripDocumentIdProofSection(html) {
  return String(html || "").replace(
    /<h2>\s*Consent for Submission of Identity[\s\S]*?<p class="document-note">[\s\S]*?<\/p>\s*/i,
    ""
  );
}

export function getFacilityDisplayName() {
  return ss("facility_display_name", "HTMATE Hospital");
}

export function getFacilityAddress() {
  return [
    ss("facilityAddressLine1"),
    ss("facilityAddressLine2"),
    ss("facilityAddressLine3"),
  ]
    .map((line) => String(line || "").trim())
    .filter(Boolean)
    .join(", ");
}

function buildFacilityAddressSection() {
  const address = getFacilityAddress();
  return address ? `<div class="facility-address">${escapeHtml(address)}</div>` : "";
}

function formatAdmittingDoctor(encounter = {}) {
  const parts = [encounter.department, encounter.admittingDoctorName].filter(Boolean);
  return parts.length ? parts.join(" — ") : "—";
}

function formatPatientAddress(addresses = []) {
  const address = addresses[0] || {};
  const parts = [address.line1, address.city, address.state, address.pincode]
    .map((part) => String(part || "").trim())
    .filter(Boolean);
  return parts.length ? parts.join(", ") : "—";
}

function buildTemplateValues(patient = {}) {
  const encounter = patient.encounter || {};
  const age = calcAge(patient.dob);
  const facilityName = escapeHtml(getFacilityDisplayName());
  const showIdProof = hasPatientDocuments(patient);
  const documentIdProofRows = showIdProof ? buildDocumentIdProofRows(patient) : "";

  return {
    facilityName,
    facilityAddressSection: buildFacilityAddressSection(),
    uhid: escapeHtml(dash(patient.uhid)),
    title: escapeHtml(dash(patient.title)),
    name: escapeHtml(dash(patient.name)),
    gender: escapeHtml(dash(patient.gender)),
    age: escapeHtml(age ?? "—"),
    dob: escapeHtml(dash(patient.dob)),
    primaryMobile: escapeHtml(dash(patient.primaryMobile)),
    nationality: escapeHtml(dash(patient.nationality)),
    religion: escapeHtml(dash(patient.religion)),
    address: escapeHtml(formatPatientAddress(patient.addresses)),
    admittingDoctor: escapeHtml(formatAdmittingDoctor(encounter)),
    financialClass: escapeHtml(dash(encounter.financialClass)),
    payer: escapeHtml(dash(encounter.payer)),
    documentIdProofRows,
    documentIdProofSection: showIdProof
      ? buildDocumentIdProofSection(patient, facilityName)
      : "",
  };
}

export function renderConsentForm(template, patient) {
  const source = template || DEFAULT_CONSENT_FORM_TEMPLATE;
  const values = buildTemplateValues(patient);

  const html = Object.entries(values).reduce(
    (next, [key, value]) => next.replaceAll(`{{${key}}}`, value),
    source
  );

  if (!hasPatientDocuments(patient) && html.includes(DOCUMENT_ID_PROOF_HEADING)) {
    return stripDocumentIdProofSection(html);
  }

  return html;
}
